import { TAG_TYPES } from '@/lib/api/tags';
import { processServerError } from '@/lib/api/listQuery';
import { notifyOnSuccess } from '@/lib/api/notifyOnSuccess';
import {
  buildActivityLogsQueryParams,
  buildInviteDto,
  buildUpdateUserDto,
  buildUsersQueryParams,
  processActivityLogsPage,
  processUsersPage,
} from '../mappers/adminMappers';

// REST only — iscc-backend has no GraphQL for users, invitations or the activity log.
const PATHS = {
  users: '/access-manager/users',
  user: (id) => `/access-manager/users/${id}`,
  invite: '/auth/invite-user',
  reminder: (id) => `/auth/invitation-reminder/${id}`,
  // `DELETE /access-manager/users/:id` queries a column that does not exist; this one works.
  remove: (id) => `/auth/remove-user/${id}`,
  activityLogs: '/activity-logs',
};

const usersListTag = { type: TAG_TYPES.User, id: 'LIST' };
const activityListTag = { type: TAG_TYPES.ActivityLog, id: 'LIST' };

export const adminEndpoints = (build) => ({
  getAccessUsers: build.query({
    query: (state) => ({ url: PATHS.users, params: buildUsersQueryParams(state) }),
    transformResponse: processUsersPage,
    providesTags: (result) =>
      result
        ? [...result.rows.map((row) => ({ type: TAG_TYPES.User, id: row.id })), usersListTag]
        : [usersListTag],
  }),

  inviteUser: build.mutation({
    query: (values) => ({ url: PATHS.invite, method: 'POST', body: buildInviteDto(values) }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ADMIN_INVITE_SENT'),
    invalidatesTags: [usersListTag, activityListTag],
  }),

  updateUser: build.mutation({
    query: ({ id, values }) => ({
      url: PATHS.user(id),
      method: 'PUT',
      body: buildUpdateUserDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ADMIN_USER_UPDATED'),
    invalidatesTags: (result, error, { id }) => [
      { type: TAG_TYPES.User, id },
      usersListTag,
      activityListTag,
    ],
  }),

  // A GET that re-sends the invite email; nothing cached changes.
  sendInvitationReminder: build.mutation({
    query: (id) => ({ url: PATHS.reminder(id) }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ADMIN_REMINDER_SENT'),
  }),

  removeUser: build.mutation({
    query: (id) => ({ url: PATHS.remove(id), method: 'DELETE' }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ADMIN_USER_REMOVED'),
    invalidatesTags: [usersListTag, activityListTag],
  }),

  // Same route as removeUser — deleting a pending user is the only revoke the server has.
  revokeInvitation: build.mutation({
    query: (id) => ({ url: PATHS.remove(id), method: 'DELETE' }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ADMIN_INVITATION_REVOKED'),
    invalidatesTags: [usersListTag, activityListTag],
  }),

  getActivityLogs: build.query({
    query: (state) => ({ url: PATHS.activityLogs, params: buildActivityLogsQueryParams(state) }),
    transformResponse: processActivityLogsPage,
    providesTags: [activityListTag],
  }),
});
